import React, { useState, useEffect } from 'react';

import RouteStyles from '../css/RouteStyles.css';

const SubcontractorInput = ({
    theme = 'light',
    header = 'Subcontractor Details',
    isOpen,
    setIsOpen,
    handleSubmit,
    onCancel,
    initialValues,
    savedSubcontractors = [], // optional list of saved sub-contractors
}) => {
    const emptyFields = { company: '', price: '', ref: '' };
    const [fields, setFields] = useState(initialValues || emptyFields);
    const [localErrors, setLocalErrors] = useState({});
    const [useList, setUseList] = useState(false);
    const [selectedId, setSelectedId] = useState('');

    useEffect(() => {
        setFields(initialValues || emptyFields);
        setLocalErrors({});
        setUseList(false);
        setSelectedId('');
    }, [initialValues, isOpen]);

    const handleListSelect = (e) => {
        const id = e.target.value;
        setSelectedId(id);
        if (id) {
            const match = savedSubcontractors.find(s => s.id === id);
            if (match) {
                setFields(f => ({ ...f, company: match.company_name }));
            }
        } else {
            setFields(f => ({ ...f, company: '' }));
        }
    };

    // Validation helper
    const validate = (fields) => {
        const errs = {};
        if (!fields.company || fields.company.length < 2) {
            errs.company = 'Company name required.';
        }
        if (!fields.price || !/^\d+(\.\d{1,2})?$/.test(fields.price)) {
            errs.price = 'Price must be a valid amount (e.g. 150.00).';
        }
        if (!fields.ref || fields.ref.length < 2) {
            errs.ref = 'Reference required.';
        }
        return errs;
    };

    const submit = () => {
        const err = validate(fields);
        if (Object.keys(err).length === 0) {
            handleSubmit(fields.company, fields.price, fields.ref);
        } else {
            setLocalErrors(err);
        }
    };

    if (!isOpen) return null;

    // Modal theme styles
    const modalBg = theme === 'dark' ? '#222' : '#fff';
    const modalColor = theme === 'dark' ? '#fff' : '#222';
    const modalBorder = theme === 'dark' ? '1px solid #555' : '1px solid #ccc';
    const inputBg = theme === 'dark' ? '#333' : '#fff';
    const inputColor = theme === 'dark' ? '#fff' : '#222';
    const tabActiveBg = theme === 'dark' ? '#444' : '#e8f4fd';
    const tabActiveColor = '#1997eb';

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}>
            <div style={{
                background: modalBg,
                color: modalColor,
                border: modalBorder,
                borderRadius: 12,
                padding: '28px 32px',
                width: 420,
                maxWidth: '94vw',
                maxHeight: '88vh',
                overflowY: 'auto',
                boxShadow: '0 4px 24px rgba(0,0,0,0.35)',
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
            }}>
                <h4 style={{ marginBottom: 16, textAlign: 'center', fontWeight: 700 }}>{header}</h4>

                {/* Mode toggle — only shown if saved list provided */}
                {savedSubcontractors.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', marginBottom: 18, borderRadius: 8, overflow: 'hidden', border: `1px solid ${theme === 'dark' ? '#555' : '#ccc'}` }}>
                        <button
                            style={{
                                margin: 0, padding: '9px 0', border: 'none', borderRight: `1px solid ${theme === 'dark' ? '#555' : '#ccc'}`, cursor: 'pointer', fontWeight: 600, fontSize: 13,
                                background: !useList ? tabActiveBg : (theme === 'dark' ? '#2a2a2a' : '#f0f0f0'),
                                color: !useList ? tabActiveColor : modalColor,
                            }}
                            onClick={() => setUseList(false)}
                        >
                            Manual Input
                        </button>
                        <button
                            style={{
                                margin: 0, padding: '9px 0', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                                background: useList ? tabActiveBg : (theme === 'dark' ? '#2a2a2a' : '#f0f0f0'),
                                color: useList ? tabActiveColor : modalColor,
                            }}
                            onClick={() => setUseList(true)}
                        >
                            Select from List
                        </button>
                    </div>
                )}

                {/* Saved sub-contractor selector */}
                {useList && (
                    <div style={{ marginBottom: 14 }}>
                        <label style={{ display: 'block', fontWeight: 500, fontSize: 13, marginBottom: 4 }}>Saved Sub-Contractor</label>
                        <select
                            value={selectedId}
                            onChange={handleListSelect}
                            style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: `1px solid ${theme === 'dark' ? '#555' : '#ccc'}`, background: inputBg, color: inputColor, fontSize: 14 }}
                        >
                            <option value="">-- Select --</option>
                            {savedSubcontractors.map(s => (
                                <option key={s.id} value={s.id}>{s.company_name}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontWeight: 500, fontSize: 13, marginBottom: 4 }}>Company Name</label>
                    <input
                        type="text"
                        value={fields.company}
                        onChange={e => setFields(f => ({ ...f, company: e.target.value }))}
                        style={{ width: '100%', background: inputBg, color: inputColor, borderRadius: 6, border: `1px solid ${localErrors.company ? '#e74c3c' : (theme === 'dark' ? '#555' : '#ccc')}`, padding: '8px 12px', fontSize: 14 }}
                        placeholder="Company"
                        readOnly={useList && !!selectedId}
                    />
                    {localErrors.company && <span style={{ color: '#e74c3c', fontSize: 12 }}>{localErrors.company}</span>}
                </div>

                <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontWeight: 500, fontSize: 13, marginBottom: 4 }}>Price (£)</label>
                    <input
                        type="text"
                        value={fields.price}
                        onChange={e => setFields(f => ({ ...f, price: e.target.value.replace(/[^\d.]/g, '') }))}
                        placeholder="0.00"
                        style={{ width: '100%', background: inputBg, color: inputColor, borderRadius: 6, border: `1px solid ${localErrors.price ? '#e74c3c' : (theme === 'dark' ? '#555' : '#ccc')}`, padding: '8px 12px', fontSize: 14 }}
                    />
                    {localErrors.price && <span style={{ color: '#e74c3c', fontSize: 12 }}>{localErrors.price}</span>}
                </div>

                <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', fontWeight: 500, fontSize: 13, marginBottom: 4 }}>Reference</label>
                    <input
                        type="text"
                        value={fields.ref}
                        onChange={e => setFields(f => ({ ...f, ref: e.target.value }))}
                        placeholder="Reference"
                        style={{ width: '100%', background: inputBg, color: inputColor, borderRadius: 6, border: `1px solid ${localErrors.ref ? '#e74c3c' : (theme === 'dark' ? '#555' : '#ccc')}`, padding: '8px 12px', fontSize: 14 }}
                    />
                    {localErrors.ref && <span style={{ color: '#e74c3c', fontSize: 12 }}>{localErrors.ref}</span>}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <button className="btn btn-secondary" style={{ minWidth: 90 }} onClick={() => { setIsOpen(false); setFields(emptyFields); setLocalErrors({}); if (onCancel) onCancel(); }}>Cancel</button>
                    <button className="btn btn-primary" style={{ minWidth: 90 }} onClick={submit}>Submit</button>
                </div>
            </div>
        </div>
    );
};

export default SubcontractorInput;

